/**
 * Unified Authentication — Re-export Layer
 *
 * Re-exports from @agion/auth, keeping admin-ui-specific exports (ROLE_PAGES, etc.).
 * All 30+ import sites use this path — the re-export keeps them stable.
 *
 * @agion/auth's useAuth is a module-level Zustand store with native
 * .getState(), .setState(), .subscribe() — no bridge or shim needed.
 */

import React from 'react'
import type { SystemRole } from '@agion/auth'

// ─── Re-exports from @agion/auth (no change at call sites) ───

export { isPlatformAdmin, useAuth } from '@agion/auth'

export type { AuthUser as User, AuthUser, SystemRole } from '@agion/auth'
export type { AuthState as AuthContextType } from '@agion/auth'
export {
  canChangeUserRole,
  canDeleteUser,
  canDeleteOrganization,
  isReadOnlyRole,
  isViewingOwnProfile,
  hasGovernancePermission,
  hasOrganizationPermission,
} from '@agion/auth'

// ─── Admin-ui specific (stays here) ───

/** Role-based page access — admin-ui specific page list */
export const ROLE_PAGES: Record<SystemRole, string[]> = {
  owner: ['control-center', 'governance', 'missions', 'fleet', 'settings', 'analytics', 'organization'],
  admin: ['control-center', 'governance', 'missions', 'fleet', 'settings', 'analytics'],
  manager: ['control-center', 'missions', 'fleet', 'analytics'],
  user: ['tasks', 'missions'],
  auditor: ['control-center', 'governance', 'missions', 'fleet', 'analytics'],
  // A Guest (external member, ADR-20260930) works in the chat app only.
  guest: [],
}

export const canAccessPage = (userRole: SystemRole, page: string): boolean => {
  return ROLE_PAGES[userRole]?.includes(page) ?? false
}

/** BFF pattern — no client-side token storage */
export const getAuthToken = (): string | null => {
  return null
}

/** Debug info for development */
export const getAuthEnvironmentInfo = () => ({
  environment: import.meta.env.MODE || 'production',
  apiBaseUrl: import.meta.env['VITE_API_BASE_URL'] || '/v1',
  isDemo: import.meta.env['VITE_DEMO_MODE'] === 'true',
  useMockData: import.meta.env['VITE_USE_MOCK_DATA'] === 'true',
  mockAuthEnabled: import.meta.env['VITE_MOCK_AUTH'],
  authDisabled: import.meta.env['VITE_AUTH_DISABLED'],
})

/** Dummy AuthProvider for backward compatibility (real one is in App.tsx) */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => children
